import { useEffect, useMemo, useState } from 'react';
import { useAddCompetenceToUserMutation, useGetCompetencesQuery, useGetCurrentUserQuery } from 'utils/wemble-api';
import organization from 'utils/organization';
import FilterSelect from '../FilterSelect';
import { UserDto } from 'utils/wemble-api.generated';
import { useCardTableStyles } from './styles';
import SegmentedToggle from '../SegmentedToggle';
import CompetenceGrid from './CompetenceGrid';
import CompetenceOverview from './CompetenceOverview';
import { ALL_CATEGORIES, CategorySelect, categoryOptions } from './competenceTree';

type CompetencesView = 'current' | 'grid';

// Global per-browser preference, not per person: whoever prefers the grid wants
// it on every profile they open.
const VIEW_KEY = 'wemble_competences_view';

const UserCompetencesTable = ({ user }: { user: UserDto }) => {
  const classes = useCardTableStyles();
  const { data: competences } = useGetCompetencesQuery();
  const { data: currentUser } = useGetCurrentUserQuery();
  const [addCompetence] = useAddCompetenceToUserMutation();

  const org = organization(currentUser?.company);
  const canEdit = !!(currentUser?.admin || currentUser?.companyAdmin || currentUser?.administratorPriviliges || (!org.disableCompetencesEditSelf && currentUser?._id === user._id));

  // The Grid is the default; once someone switches, their choice is kept (per
  // browser, for every profile they open).
  const [chosenView, setChosenView] = useState<CompetencesView | null>(() => {
    const stored = localStorage.getItem(VIEW_KEY);
    return stored === 'grid' || stored === 'current' ? stored : null;
  });
  const view: CompetencesView = chosenView ?? 'grid';
  const handleViewChange = (next: CompetencesView) => {
    setChosenView(next);
    localStorage.setItem(VIEW_KEY, next);
  };

  // One category selection shared by both views, so switching between them
  // keeps you on the category you were looking at. Both start on every
  // category, ordered by how many competencies the person holds in each.
  const [category, setCategory] = useState<string | null>(null);
  const categories = useMemo(() => categoryOptions(competences || []), [competences]);
  const selectedCategory = category ?? ALL_CATEGORIES;
  // Group order follows how many competencies the person holds — but it is
  // taken from a snapshot, refreshed only when the person, category or view
  // changes. Otherwise setting a level reorders the groups under the cursor
  // and the next click lands on a different competency.
  const [orderUser, setOrderUser] = useState(user);
  useEffect(() => {
    setOrderUser(user);
  }, [user._id, selectedCategory, view]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={classes.section}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <h2>Competencies</h2>
        {/* The category filter and the view toggle do unrelated jobs, so they
            sit further apart than the toggle's own buttons do. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 22, minWidth: 0 }}>
          <CategorySelect options={categories} value={selectedCategory} onChange={setCategory} />
          <SegmentedToggle
            options={[
              { value: 'grid', label: 'All', tooltip: 'Every competency, with the levels they have' },
              { value: 'current', label: 'Current', tooltip: 'Only the competencies they have' },
            ]}
            value={view}
            onChange={handleViewChange}
          />
        </div>
      </div>

      {view === 'grid' ? (
        <CompetenceGrid
          user={user}
          orderUser={orderUser}
          competences={competences || []}
          category={selectedCategory}
          canEdit={canEdit}
        />
      ) : (
        <CompetenceOverview
          user={user}
          orderUser={orderUser}
          competences={competences || []}
          category={selectedCategory}
          canEdit={canEdit}
        />
      )}

      {/* The grid already lists every competence — adding one there is just
          clicking a level on an empty row. */}
      {canEdit && view === 'current' && (
        <div style={{ marginTop: 12 }}>
          <FilterSelect
            options={[
              ...competences?.filter(competence => competences.find(c => c.parent?._id === competence._id)).map(c => ({
                label: (c.parent ? (c.parent.name + " - " + c.name) : c.name),
                options: competences?.filter(competence => competence.parent?._id === c._id && !competence.root && !user.skills?.find(skill => typeof skill !== 'string' && typeof skill.competence !== 'string' && skill.competence?._id == competence._id))
                  ?.map((competence) => {
                    return {
                      label: competence.name,
                      value: competence._id,
                      type: 'competence',
                    };
                  }),
              })) || []
              , {
                label: 'Competencies',
                options: competences?.filter(competence => !competence.root && !competence.parent && !user.skills?.find(skill => typeof skill !== 'string' && typeof skill.competence !== 'string' && skill.competence?._id == competence._id)).map((competence) => {
                  return {
                    label: competence.name,
                    value: competence._id,
                    type: 'competence',
                  };
                }),
              }].filter((v) => v !== null)}
            onChange={(value) => {
              user._id && value[0] && value[0].value && addCompetence({ userId: user._id, id: value[0].value })
              window.document.activeElement && window.document.activeElement instanceof HTMLElement && window.document.activeElement.blur()
            }}
            defaultValue={[]}
            placeholder={'Add competency'}
            small
          />
        </div>
      )}
    </div>
  );
};

export default UserCompetencesTable;
